import dayjs from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { useCallback } from 'react';
import * as yup from 'yup';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { queryClient } from '@/modules/common/query.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import {
  ISSUER_NAV_LIST_QUERY_KEY,
  useNavUpdateMutation,
} from '@/modules/issuer-dashboard/queries/nav.tsx';
import {
  BaseModalProps,
  Button,
  CalendarInput,
  CalendarInputProps,
  ModalContentWrapper,
  TextInputField,
} from 'ui';
import { UpdateNavRequest } from 'api';

export const validationSchema = (isActualAndHistoricalPrice: boolean) => {
  return yup.object<UpdateNavRequest>({
    changes_date: yup.string().trim().required('This field is required'),
    price_per_share: yup
      .number()
      .typeError('Price must be a number')
      .required('This field is required'),
    justification: isActualAndHistoricalPrice
      ? yup.string().trim().required('This field is required')
      : yup.string().trim().nullable(),
  });
};

const initialValues: UpdateNavRequest = {
  id: 0,
  changes_date: '',
  price_per_share: 1,
  justification: '',
};

export interface IssuerEditNavModalProps extends BaseModalProps {
  id: number;
  changes_date: string;
  price_per_share: number;
  isActualAndHistoricalPrice: boolean;
}

export const IssuerEditNavModal = ({
  open,
  onClose,
  id,
  changes_date,
  price_per_share,
  isActualAndHistoricalPrice,
}: IssuerEditNavModalProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const formik = useFormik<UpdateNavRequest>({
    initialValues: {
      ...initialValues,
      id,
      changes_date,
      price_per_share,
    },
    validationSchema: validationSchema(isActualAndHistoricalPrice),
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async values => {
      await updateNav(values);
      await queryClient.resetQueries([ISSUER_NAV_LIST_QUERY_KEY]);
      handleCloseModalWithResetForm();
    },
    enableReinitialize: true,
  });

  const { mutateAsync: updateNav, isLoading } = useNavUpdateMutation({
    setErrors: formik.setErrors,
  });

  const handleCloseModalWithResetForm = useCallback(() => {
    formik.resetForm();
    onClose();
  }, [formik, onClose]);

  const handleChangeDate: CalendarInputProps['onChange'] = date => {
    const currentDate = date.format('YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]');

    formik.setFieldValue('changes_date', currentDate);
    formik.setFieldError('changes_date', '');
  };

  return (
    <ModalContentWrapper
      open={open}
      title={`You are editing historical ${getRegisterTypeStrings('nav')}`}
      onClose={handleCloseModalWithResetForm}
    >
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col space-y-10" onSubmit={preventFormDefault}>
          <div className="w-full flex flex-col space-y-8">
            {isActualAndHistoricalPrice && (
              <span className="text-sm leading-[22px] font-[Inter]">
                Please add your justification (will be shown in Update Log and Investor
                notifications)
              </span>
            )}
            <CalendarInput
              label="Active From"
              name="changes_date"
              showTime={false}
              format="DD-MM-YYYY"
              onChange={handleChangeDate}
              value={formik.values.changes_date ? dayjs(formik.values.changes_date) : undefined}
              error={formik.errors.changes_date}
            />
            <TextInputField name="price_per_share" label="Price per share/unit" type="number" />

            {isActualAndHistoricalPrice && (
              <TextInputField
                name="justification"
                label="Your justification for historical NAV change"
                placeholder="Enter your text"
                textarea={true}
              />
            )}
          </div>
          <Button onClick={formik.submitForm} loading={isLoading}>
            Save changes
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
